import type { Movimiento, Sugerencia } from "../api/tipos.ts";
import type { Decision } from "../ganchos/datos.ts";
import { cargaDecision } from "./decision-modelo.ts";
import { instanteDe } from "../formato.ts";
import { ejesDe } from "./ejes.ts";

/**
 * Qué se puede hacer con un pago desde su fila, sin abrirlo.
 *
 * La bandeja en filas cabe quince pagos por pantalla, y con eso aparece un
 * riesgo que la tarjeta grande escondía: confirmar rápido y dejar la factura
 * fuera justo cuando sí se podía ligar. Para Lorus eso no es cosmético —sin
 * factura, AvanPOS no asienta el abono de una venta a crédito— (30-sep-2026).
 *
 * Dos reglas (SPEC-029):
 * - **el atajo solo confirma cuando la factura ya está resuelta o cuando no hay
 *   ninguna que ligar** (`viaDeFactura`);
 * - **y nunca cuando algo comprobable está en contra**: nombre, monto o fecha en
 *   ⚠ (§6, P-4). Entonces la fila dice «Revisar» y abre el pago.
 *
 * Las facturas candidatas las calcula el servidor y viajan con el pago
 * (`sugerencias`, §4.3). Antes se calculaban aquí con la cartera, y el cajero
 * —que no la recibe— confirmaba siempre sin factura.
 *
 * Vive aparte de React por la misma razón que `decision-modelo.ts`: un botón
 * que dice «Sí, FVEL1917» y manda otra cosa se ve perfecto.
 */
export type Via =
  /** Alguien ya lo había decidido y los datos cambiaron, o la clave es ambigua. */
  | { tipo: "revisar" }
  /** No hay propuesta que confirmar: hay que decir de quién es. */
  | { tipo: "identificar"; conflicto: boolean }
  /** Regla 1 — la propuesta ya trae la factura. */
  | { tipo: "trae"; factura: string; desde: "abono" | "cartera" }
  /** Regla 2 — la propuesta no la trae, pero hay una que encaja. */
  | { tipo: "sugerida"; factura: Sugerencia }
  /** Regla 3 — varias podrían ser: no hay atajo, se elige. */
  | { tipo: "varias"; candidatas: Sugerencia[] }
  /** Regla 4 — no hay nada que ligar: confirmar no olvida nada. */
  | { tipo: "ninguna" };

export type Atajo =
  | Via
  /** Hay propuesta, pero algo comprobable está en contra: se abre el pago. */
  | { tipo: "evidencia"; via: Via };

/** Los grupos de la bandeja: cada uno se resuelve de una forma distinta. */
export type Grupo = "confirmar" | "identificar" | "conflicto" | "cambio";

export const GRUPOS: { grupo: Grupo; texto: string }[] = [
  { grupo: "cambio", texto: "Datos cambiaron" },
  { grupo: "confirmar", texto: "Falta confirmar" },
  { grupo: "conflicto", texto: "Puede ser de varios" },
  { grupo: "identificar", texto: "Falta decir de quién es" },
];

export function grupoDe(m: Movimiento): Grupo {
  if (m.origen === "humano-obsoleto" || m.origen === "clave-ambigua") return "cambio";
  if (m.estado === "CONFLICTO") return "conflicto";
  if (m.estado === "SIN_IDENTIFICAR") return "identificar";
  return "confirmar";
}

const hayAbono = (m: Movimiento) => (m.abonosClaves?.length ?? 0) > 0;

/** Qué pide el «sí» de este pago respecto a la factura. Lo usan la fila y el detalle. */
export function viaDeFactura(m: Movimiento): Via {
  const grupo = grupoDe(m);
  if (grupo === "cambio") return { tipo: "revisar" };
  if (grupo === "conflicto") return { tipo: "identificar", conflicto: true };

  // Una factura de la cartera que propuso por sí sola (SPEC-028 §9): no hay
  // abono que confirmar, así que se etiqueta con ella.
  if (!hayAbono(m)) {
    return m.estado === "POR_CONFIRMAR" && m.factura?.trim() && m.cliente?.trim()
      ? { tipo: "trae", factura: m.factura.trim(), desde: "cartera" }
      : { tipo: "identificar", conflicto: false };
  }

  if (m.factura?.trim()) return { tipo: "trae", factura: m.factura.trim(), desde: "abono" };

  const candidatas = m.sugerencias ?? [];
  if (candidatas.length === 1 && candidatas[0]) return { tipo: "sugerida", factura: candidatas[0] };
  if (candidatas.length > 1) return { tipo: "varias", candidatas };
  return { tipo: "ninguna" };
}

/** ¿Hay algo comprobable en contra? Nombre, monto o fecha en ⚠. */
export function evidenciaEnContra(m: Movimiento): boolean {
  return ejesDe(m).some((e) => e.clave !== "factura" && e.cierre === "hueco");
}

/** Lo que ofrece la fila. */
export function atajoDe(m: Movimiento): Atajo {
  const via = viaDeFactura(m);
  const confirma = via.tipo === "trae" || via.tipo === "sugerida" || via.tipo === "ninguna";
  return confirma && evidenciaEnContra(m) ? { tipo: "evidencia", via } : via;
}

/**
 * Lo que manda el botón principal de la fila. `null` = ese atajo no confirma
 * nada: abre el detalle.
 */
export function cargaDeAtajo(m: Movimiento, atajo: Atajo): Decision | null {
  switch (atajo.tipo) {
    case "trae":
      return atajo.desde === "cartera"
        ? cargaDecision({
            clave: m.clave,
            accion: "ETIQUETAR",
            cliente: m.cliente ?? "",
            clienteNit: m.clienteNit ?? "",
            facturas: [atajo.factura],
          })
        : cargaDecision({ clave: m.clave, accion: "CONFIRMAR" });
    case "sugerida":
      return cargaDecision({ clave: m.clave, accion: "CONFIRMAR", facturas: [atajo.factura.numero] });
    case "ninguna":
      return cargaDecision({ clave: m.clave, accion: "CONFIRMAR" });
    default:
      return null;
  }
}

/** El texto del botón principal. Dice la factura: lo que se guarda es lo que dice. */
export function rotuloDeAtajo(atajo: Atajo): string {
  switch (atajo.tipo) {
    case "trae":
      return `Sí, ${atajo.factura}`;
    case "sugerida":
      return `Sí, con ${atajo.factura.numero}`;
    case "varias":
      return "Elegir factura";
    case "ninguna":
      return "Sí";
    case "identificar":
      return atajo.conflicto ? "Elegir…" : "Identificar…";
    case "evidencia":
    case "revisar":
      return "Revisar";
  }
}

/** Por qué se confirma sin factura habiendo una que encaja. Queda en la nota. */
export const RAZONES_SIN_FACTURA = [
  { clave: "anticipo", texto: "Pago anticipado" },
  { clave: "varias", texto: "Abona a varias facturas" },
  { clave: "no-aparece", texto: "La factura no aparece" },
] as const;

export type RazonSinFactura = (typeof RAZONES_SIN_FACTURA)[number]["clave"];

/**
 * La nota de una confirmación sin factura. «La factura no aparece» exige texto:
 * es el caso en que alguien tendrá que ir a buscarla, y sin decir cuál no hay
 * qué buscar. Devuelve null si falta.
 */
export function notaSinFactura(razon: RazonSinFactura, texto: string): string | null {
  const r = RAZONES_SIN_FACTURA.find((x) => x.clave === razon)?.texto ?? razon;
  const t = texto.trim();
  if (razon === "no-aparece" && !t) return null;
  return `Sin factura — ${r}${t ? `: ${t}` : ""}`;
}

/**
 * El orden de la bandeja: prioridad primero y fecha después, no al revés. Lo
 * que ya se había decidido y cambió tiene que seguir arriba del todo aunque sea
 * viejo —es el caso que puede costar plata—. Dentro de cada grupo, del más
 * nuevo al más viejo, que es como se lee una bandeja. Lo usa también «Lo más
 * urgente» del Resumen, que tiene que enseñar lo mismo que la bandeja arriba.
 */
const PRIORIDAD: Record<string, number> = {
  "humano-obsoleto": 0,
  "clave-ambigua": 1,
  pendiente: 2,
};

export const enOrdenDeBandeja = (pendientes: Movimiento[]): Movimiento[] =>
  [...pendientes].sort(
    (a, b) =>
      (PRIORIDAD[a.origen] ?? 9) - (PRIORIDAD[b.origen] ?? 9) || instanteDe(b.fecha) - instanteDe(a.fecha),
  );
